import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { InputAdornment } from '@mui/material';
import { SearchIcon } from '../../../../../lib/icons/SearchIcon';
import { useAttributeLabel } from '../../../../../lib/hooks/labels';
import { ATTRIBUTES } from '../../../../../lib/constants/attributes';
import { COMMON_LABELS } from '../../../../../lib/constants/labelKeys';
import { ClassType } from '../../../../../lib/utils/cssUtils';
import Input from '../../../../common/Input/Input';
import { CY_SEARCH_AND_SORT } from '../../../../../lib/constants/cypress';

export type FilterSearchBoxProps = {
  value: string;
  onChange: (value: string) => void;
  className?: ClassType;
  customPlaceholderKey?: string;
  withSearch?: boolean;
  dataTestId?: string;
  isLoading?: boolean;
  disableBlurring?: boolean;
};

const FilterSearchBox = ({
  dataTestId,
  value,
  onChange,
  className,
  customPlaceholderKey,
  withSearch,
  isLoading = false,
  disableBlurring = false,
}: FilterSearchBoxProps): ReactNode => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const labels = useAttributeLabel(
    [ATTRIBUTES.PLACEHOLDER, ATTRIBUTES.ARIA_LABEL],
    customPlaceholderKey ?? COMMON_LABELS.SEARCH.TRAINING,
  );

  const onBlurHandler = useCallback(() => {
    if (!isLoading) {
      setIsFocused(false);
    }
  }, [isLoading]);

  const handleOnChange = (newValue: string) => {
    if (localValue !== newValue) {
      setLocalValue(newValue);
      onChange(newValue);
    }
  };

  useEffect(() => {
    if (localValue !== value) {
      setLocalValue(value);
    }
  }, [value]);

  useLayoutEffect(() => {
    if (isLoading && isFocused) {
      inputRef.current?.blur();
    }

    if (!isLoading && isFocused) {
      inputRef.current?.focus();
    }
  }, [isLoading]);

  if (withSearch === false) {
    return null;
  }

  return (
    <Input
      {...(className && { className })}
      inputRef={inputRef}
      data-testid={dataTestId}
      value={localValue}
      inputProps={{ 'data-cy': CY_SEARCH_AND_SORT.SEARCH_INPUT, ...labels }}
      startAdornment={
        <InputAdornment position="start">
          <SearchIcon />
        </InputAdornment>
      }
      onChange={event => handleOnChange(event.target.value)}
      {...(!disableBlurring && { onFocus: () => setIsFocused(true), onBlur: onBlurHandler })}
    />
  );
};

export default FilterSearchBox;
